import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

type AlertType = 'information' | 'warning' | 'danger' | 'success';

export interface AlertProps {
  readonly type?: AlertType;
  readonly title?: string;
  readonly description?: string;
  readonly children?: ReactNode;
  readonly className?: string;
}

const typeStyles: Record<AlertType, string> = {
  information: 'border-base-accent-300 bg-base-accent-100 text-base-accent-700',
  warning: 'border-base-warning-300 bg-base-warning-100 text-base-warning-700',
  danger: 'border-base-danger-300 bg-base-danger-100 text-base-danger-700',
  success: 'border-base-success-300 bg-base-success-100 text-base-success-700',
};

const iconClassName = 'size-xxs shrink-0';

const typeIcons: Record<AlertType, ReactNode> = {
  information: <InformationCircleIcon className={iconClassName} />,
  warning: <ExclamationTriangleIcon className={iconClassName} />,
  danger: <ExclamationCircleIcon className={iconClassName} />,
  success: <CheckCircleIcon className={iconClassName} />,
};

export function Alert({
  type = 'information',
  title,
  description,
  children,
  className = '',
}: AlertProps) {
  const hasStructured = Boolean(title || description);

  return (
    <div
      role="alert"
      className={`flex items-start gap-mini rounded-sm border p-mini ts-paragraph-xs ${typeStyles[type]} ${className}`}
    >
      {hasStructured ? (
        <>
          {typeIcons[type]}

          <div className="flex flex-col gap-quark">
            {title && <span className="ts-heading-xs">{title}</span>}

            {description && <span>{description}</span>}
          </div>
        </>
      ) : (
        children
      )}
    </div>
  );
}
